// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Devices as DevicesIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  People as PeopleIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { fleetAdminAPI } from '../services/api';

interface FleetAnalyticsData {
  total_agents: number;
  online_agents: number;
  total_users: number;
  total_jobs: number;
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color = 'primary.main' }) => (
  <Paper sx={{ p: 3, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ color, mr: 1.5, display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="h3" sx={{ fontWeight: 700, color }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const FleetAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<FleetAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [endpointUnavailable, setEndpointUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnalytics = async () => {
    setLoading(true);
    setEndpointUnavailable(false);
    setError(null);
    try {
      const data = await fleetAdminAPI.getFleetAnalytics();
      setAnalytics(data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 501 || status === 422) {
        setEndpointUnavailable(true);
      } else {
        setError('Error al cargar datos de analytics.');
        setEndpointUnavailable(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const offlineAgents = analytics
    ? analytics.total_agents - analytics.online_agents
    : 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Fleet - Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Resumen consolidado de todos los clientes con fleet habilitado.
        </Typography>
      </Box>

      {endpointUnavailable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          El endpoint de analytics fleet aún no está disponible — se implementará en una próxima actualización.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title="Total Agentes"
                value={analytics?.total_agents ?? '—'}
                subtitle="Agentes registrados"
                icon={<DevicesIcon />}
                color="primary.main"
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title="Agentes Online"
                value={analytics?.online_agents ?? '—'}
                subtitle={analytics ? `${offlineAgents} offline` : undefined}
                icon={<OnlineIcon />}
                color="success.main"
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title="Total Usuarios"
                value={analytics?.total_users ?? '—'}
                subtitle="Usuarios fleet sincronizados"
                icon={<PeopleIcon />}
                color="info.main"
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard
                title="Total Trabajos"
                value={analytics?.total_jobs ?? '—'}
                subtitle="Trabajos de impresión registrados"
                icon={<PrintIcon />}
                color="secondary.main"
              />
            </Grid>
          </Grid>

          {analytics && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Disponibilidad de Agentes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <OnlineIcon color="success" />
                  <Typography variant="body2">
                    Online: <strong>{analytics.online_agents}</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <OfflineIcon color="error" />
                  <Typography variant="body2">
                    Offline: <strong>{offlineAgents}</strong>
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Disponibilidad:{' '}
                    <strong>
                      {analytics.total_agents > 0
                        ? `${Math.round((analytics.online_agents / analytics.total_agents) * 100)}%`
                        : 'N/A'}
                    </strong>
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default FleetAnalytics;
